import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { Send, Youtube, X, ExternalLink, CheckCircle } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  FeedbackSnackbar,
  useIframeResizer,
  finishProcessing,
  GEMINI_MODEL,
} from './common';
import { config } from '../../../config';

interface PreviewState {
  videoId: string | null;
  thumbnailUrl: string | null;
  title: string | null;
  description: string | null;
  author: string | null;
  viewCount: string | null;
  uploadDate: string | null;
}

interface KeyPoints {
  points: string[];
}

const YouTubeSummarizer: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const playerRef = useRef<HTMLIFrameElement>(null);

  const [inputUrl, setInputUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({
    videoId: null,
    thumbnailUrl: null,
    title: null,
    description: null,
    author: null,
    viewCount: null,
    uploadDate: null,
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [keyPoints, setKeyPoints] = useState<KeyPoints | null>(null);
  const [hasFocused, setHasFocused] = useState(false);

  useIframeResizer();

  const extractYouTubeId = (videoUrl: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];

    const match = patterns.find((pattern) => videoUrl.match(pattern));
    return match ? videoUrl.match(match)?.[1] || null : null;
  };

  useEffect(() => {
    const fetchVideoInfo = async () => {
      const videoId = extractYouTubeId(inputUrl);
      if (videoId) {
        try {
          const response = await fetch(
            `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`
          );
          const data = await response.json();
          setPreview({
            videoId,
            thumbnailUrl: `https://img.youtube.com/vi/${videoId}/default.jpg`,
            title: data.title || null,
            description: data.description || null,
            author: data.author_name || null,
            viewCount: data.view_count?.toLocaleString() || null,
            uploadDate: data.upload_date || null,
          });
        } catch {
          setPreview({
            videoId,
            thumbnailUrl: `https://img.youtube.com/vi/${videoId}/default.jpg`,
            title: null,
            description: null,
            author: null,
            viewCount: null,
            uploadDate: null,
          });
        }
      } else {
        setPreview({
          videoId: null,
          thumbnailUrl: null,
          title: null,
          description: null,
          author: null,
          viewCount: null,
          uploadDate: null,
        });
      }
    };
    fetchVideoInfo();
  }, [inputUrl]);

  const handleSummarize = async () => {
    if (!preview.videoId) {
      setError(t('youtubeSummarizer.messages.error'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `You are a helpful AI assistant that provides quick summaries of YouTube videos.

Video Information:
Title: ${preview.title || 'Untitled Video'}
Channel: ${preview.author || 'Unknown Channel'}
${preview.uploadDate ? `Upload Date: ${preview.uploadDate}` : ''}
${preview.viewCount ? `Views: ${preview.viewCount}` : ''}
URL: https://www.youtube.com/watch?v=${preview.videoId}

${preview.description ? `Description:\n${preview.description}\n` : ''}

Based on all this context, provide EXACTLY 3 key points from the video. Return ONLY a raw JSON array of 3 strings, with no markdown formatting, no code blocks, and no additional text. Each string should be a concise, single-sentence key point.

Example of exact response format:
["First key point about the video","Second key point about the video","Third key point about the video"]`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        console.error('API Error:', await response.text());
        throw new Error(
          `Failed to get response from Gemini API: ${response.status}`
        );
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        throw new Error('Invalid response format from API');
      }

      try {
        const responseText = data.candidates[0].content.parts[0].text
          .trim()
          .replace(/^```json\n?/, '') // Remove opening code block
          .replace(/\n?```$/, ''); // Remove closing code block

        const points = JSON.parse(responseText);
        if (!Array.isArray(points) || points.length !== 3) {
          throw new Error('Invalid points format');
        }
        setKeyPoints({ points });
        setShowSuccess(true);
      } catch (parseError) {
        console.error('Parse Error:', parseError);
        throw new Error('Failed to parse response from API');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('youtubeSummarizer.messages.error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewSummary = () => {
    setInputUrl('');
    setShowSuccess(false);
    setError(null);
    setKeyPoints(null);
    setPreview({
      videoId: null,
      thumbnailUrl: null,
      title: null,
      description: null,
      author: null,
      viewCount: null,
      uploadDate: null,
    });
  };

  const handleDismissPreview = () => {
    setInputUrl('');
    setShowSuccess(false);
    setError(null);
    setKeyPoints(null);
    setPreview({
      videoId: null,
      thumbnailUrl: null,
      title: null,
      description: null,
      author: null,
      viewCount: null,
      uploadDate: null,
    });
  };

  const getPreviewPadding = () => {
    if (!preview.videoId) return '14px';
    if (isSmallScreen || isMediumScreen) return '48px';
    return '150px';
  };

  const getPreviewMaxWidth = () => {
    if (isSmallScreen || isMediumScreen) return '32px';
    return preview.title ? '250px' : '130px';
  };

  const handleViewFullSummary = () => {
    if (preview.videoId) {
      const videoUrl = `https://www.youtube.com/watch?v=${preview.videoId}`;
      finishProcessing(videoUrl);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        fontFamily: 'Inter',
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ position: 'relative', mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              hasFocused
                ? t('youtubeSummarizer.input.placeholder')
                : t('youtubeSummarizer.input.placeholder')
            }
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onFocus={() => setHasFocused(true)}
            disabled={isLoading || showSuccess}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !showSuccess) {
                e.preventDefault();
                handleSummarize();
              }
            }}
            helperText={
              !preview.videoId && !showSuccess
                ? t('youtubeSummarizer.input.supportText')
                : undefined
            }
            FormHelperTextProps={{
              sx: {
                mt: 1,
                ml: 0,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: showSuccess ? 'action.hover' : 'background.default',
                fontFamily: 'Inter',
                pr: getPreviewPadding(),
                fontSize: { xs: '0.875rem', sm: '1rem' },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  '& fieldset': {
                    borderColor: showSuccess ? 'divider' : 'primary.main',
                  },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderWidth: '1px !important',
                  },
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.hover',
                  opacity: 0.8,
                },
              },
            }}
            InputProps={{
              startAdornment: !preview.videoId && (
                <Youtube
                  sx={{
                    color: 'text.secondary',
                    mr: 1,
                    fontSize: '1.25rem',
                  }}
                />
              ),
            }}
          />
          {preview.videoId && (
            <Paper
              elevation={0}
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                p: 0.5,
                pr: isSmallScreen || isMediumScreen ? 0.5 : 0,
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                maxWidth: getPreviewMaxWidth(),
                height: '32px',
                overflow: 'hidden',
                zIndex: 1,
              }}
            >
              {preview.thumbnailUrl ? (
                <Box
                  component="img"
                  src={preview.thumbnailUrl}
                  alt={t('youtubeSummarizer.input.thumbnailAlt')}
                  sx={{
                    width: '32px',
                    height: '32px',
                    objectFit: 'cover',
                    borderRadius: '2px',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '2px',
                  }}
                >
                  <Youtube sx={{ fontSize: 16, color: '#FF0000' }} />
                </Box>
              )}
              {!isSmallScreen && !isMediumScreen && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '10px',
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {preview.title || t('youtubeSummarizer.input.defaultTitle')}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleDismissPreview}
                    sx={{
                      p: 0.5,
                      mr: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <X sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              )}
            </Paper>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 2 },
            mb: preview.videoId ? { xs: 3, sm: 3 } : 0,
          }}
        >
          {!showSuccess && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleSummarize}
              disabled={!inputUrl.trim() || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Send />}
              sx={{
                py: { xs: 1.5, sm: 1.75 },
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {isLoading
                ? t('youtubeSummarizer.messages.processing')
                : t('youtubeSummarizer.input.submit')}
            </Button>
          )}
          {showSuccess && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleNewSummary}
              startIcon={<Youtube />}
              sx={{
                width: '100%',
                py: { xs: 1.5, sm: 1.75 },
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              {t('youtubeSummarizer.messages.complete')}
            </Button>
          )}
        </Box>

        {!preview.videoId && !showSuccess && (
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, sm: 4 },
              color: 'text.secondary',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle
                sx={{
                  color: 'success.main',
                  fontSize: '1.25rem',
                }}
              />
              {t('youtubeSummarizer.features.free')}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle
                sx={{
                  color: 'success.main',
                  fontSize: '1.25rem',
                }}
              />
              {t('youtubeSummarizer.features.noSignup')}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle
                sx={{
                  color: 'success.main',
                  fontSize: '1.25rem',
                }}
              />
              {t('youtubeSummarizer.features.instant')}
            </Box>
          </Box>
        )}

        {preview.videoId && (
          <Box>
            {showSuccess && keyPoints && (
              <Collapse in>
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    bgcolor: 'success.light',
                    borderRadius: { xs: 2, sm: 2 },
                    color: 'success.contrastText',
                    mb: { xs: 3, sm: 3 },
                  }}
                >
                  <Box
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      '& > :not(:last-child)': {
                        mb: { xs: 2, sm: 2 },
                      },
                    }}
                  >
                    {keyPoints.points.map((point, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: { xs: 1.25, sm: 1.5 },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 22, sm: 26 },
                            height: { xs: 22, sm: 26 },
                            borderRadius: '50%',
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            fontWeight: 600,
                            mt: '2px',
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            opacity: 0.95,
                            textAlign: 'left',
                            lineHeight: { xs: 1.4, sm: 1.5 },
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            fontWeight: 400,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleViewFullSummary}
                      endIcon={
                        <ExternalLink
                          sx={{
                            fontSize: { xs: 16, sm: 18 },
                            ml: 0.5,
                          }}
                        />
                      }
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'success.dark',
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        py: { xs: 1, sm: 1.25 },
                        px: { xs: 2.5, sm: 3 },
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        '& .MuiSvgIcon-root': {
                          color: 'success.dark',
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                          '& .MuiSvgIcon-root': {
                            color: 'success.dark',
                          },
                        },
                        transition: theme.transitions.create(
                          ['background-color', 'box-shadow', 'transform'],
                          {
                            duration: theme.transitions.duration.shorter,
                          }
                        ),
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      {t('youtubeSummarizer.controls.viewFull')}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            )}

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '56.25%', // 16:9 aspect ratio
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.default',
              }}
            >
              <iframe
                ref={playerRef}
                src={`https://www.youtube.com/embed/${preview.videoId}?enablejsapi=1`}
                title={t('youtubeSummarizer.input.playerTitle')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      <FeedbackSnackbar
        open={!!error}
        message={error || t('youtubeSummarizer.messages.error')}
        severity="error"
        onClose={() => setError(null)}
      />
    </Box>
  );
};

export default YouTubeSummarizer;
